import React, { useCallback, useEffect, useState } from "react";
import { useLoading } from "../../../context/loadingContext";
import GetProductTypes from "../../../functions/reference/getProductType";
import { deleteProductType } from "../../../functions/reference/productTypeAdmin";
import ProductTypeEditModal from "../../../components/modal/productTypeEdit";
import {
  Button,
  Popconfirm,
  Row,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { bg_button, bg_shadow } from "constant/css";

const paymentTypeColor = (type) => {
  if (!type) return "default";
  if (type.includes("PURCHASE")) return "cyan";
  if (type.includes("POS")) return "purple";
  return "geekblue";
};

const ProductTypeList = () => {
  const [data, setData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { showLoading, hideLoading } = useLoading();

  const fetchData = useCallback(async () => {
    showLoading();
    try {
      const response = await GetProductTypes();
      setData(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching product types:", error);
      message.error("Жагсаалт авахад алдаа гарлаа");
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleDelete = async (id) => {
    showLoading();
    const response = await deleteProductType(id);
    hideLoading();
    if (response?.success) {
      message.success("Устгагдлаа");
      fetchData();
    } else if (response?.message) {
      message.error(response.message);
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
      fixed: "left",
    },
    {
      title: "Нэр",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "paymentType",
      dataIndex: "paymentType",
      key: "paymentType",
      width: 160,
      render: (e) => <Tag color={paymentTypeColor(e)}>{e || "—"}</Tag>,
    },
    {
      title: "settleType",
      dataIndex: "settleType",
      key: "settleType",
      width: 120,
      render: (e) =>
        e === "INSTANT" ? (
          <Tag color="green">INSTANT</Tag>
        ) : (
          <Tag color="orange">{e || "—"}</Tag>
        ),
    },
    {
      title: "Үүсгэсэн",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 160,
      render: (e) => (e ? dayjs(e).format("YYYY-MM-DD HH:mm") : "—"),
    },
    {
      title: "Зассан",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 160,
      render: (e) => (e ? dayjs(e).format("YYYY-MM-DD HH:mm") : "—"),
    },
    {
      title: "",
      key: "action",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <div className="flex gap-1">
          <Tooltip title="Засах">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Устгах уу?"
            description="Мерчант эсвэл нэхэмжлэлийн мөр холбогдсон бол устгахгүй."
            onConfirm={() => handleDelete(record.id)}
            okText="Тийм"
            cancelText="Үгүй"
          >
            <Tooltip title="Устгах">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="vatMerchantListontainer mt-3">
      <Row style={{ gap: 8, marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          className={`${bg_button} ${bg_shadow} rounded-xl`}
          onClick={openCreate}
        >
          Нэмэх
        </Button>
        <Button icon={<RedoOutlined />} onClick={fetchData}>
          Шинэчлэх
        </Button>
      </Row>
      <Table
        dataSource={data}
        columns={columns}
        pagination={false}
        rowKey="id"
        size="small"
        scroll={{ x: "max-content", y: "calc(100vh - 28vh)" }}
      />
      {modalOpen && (
        <ProductTypeEditModal
          open={modalOpen}
          close={closeModal}
          record={editing}
          refresh={fetchData}
        />
      )}
    </div>
  );
};

export default ProductTypeList;
